import React, { useState, useEffect } from "react";
import { Package, ListTree, ShieldCheck, BarChart2, Sun, Moon, LogOut } from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";

const Dashboard = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  useEffect(() => {
    document.body.className = darkMode ? "bg-dark text-white" : "bg-light text-dark";
  }, [darkMode]);

  const handleLogout = () => {
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    window.location.href = "/admin/login";
  };

  return (
    <div className="container-fluid py-3" style={{ maxHeight: "100vh", overflowY: "auto" }}>
      
      {/* ✅ Header + Buttons Responsive */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
        
        <div>
          <h1 className="fw-bold fs-3 fs-md-1">Welcome, Admin 👋</h1>
          <p className="text-muted small">
            Manage products, categories, orders & more.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? <Sun className="me-2" /> : <Moon className="me-2" />}
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>

          <button className="btn btn-danger d-flex align-items-center" onClick={handleLogout}>
            <LogOut className="me-2" /> Logout
          </button>
        </div>
      </div>

      {/* ✅ Cards Grid */}
      <div className="row g-3">
        
        {/* Products */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <Package className="text-primary me-2" />
                <h6 className="mb-0">Products</h6>
              </div>
              <p className="text-muted small mb-2">Add/Edit/Delete products.</p>
              <button className="btn btn-sm btn-link px-0" onClick={() => (window.location.href = "/admin/products")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="100">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ListTree className="text-dark me-2" />
                <h6 className="mb-0">Categories</h6>
              </div>
              <p className="text-muted small mb-2">Organize product types.</p>
              <button className="btn btn-sm btn-link px-0" onClick={() => (window.location.href = "/admin/categories")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Gift Products */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="150">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <Package className="text-primary me-2" />
                <h6 className="mb-0">Gift Products</h6>
              </div>
              <p className="text-muted small mb-2">Manage gifts & combos.</p>
              <button className="btn btn-sm btn-link px-0" onClick={() => (window.location.href = "/admin/gifts")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="200">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ShieldCheck className="text-danger me-2" />
                <h6 className="mb-0">Security</h6>
              </div>
              <p className="text-muted small mb-2">Admin auth & safety.</p>
              <button className="btn btn-sm btn-link px-0 text-danger" onClick={() => (window.location.href = "/admin/health")}>
                Settings →
              </button>
            </div>
          </div>
        </div>

        {/* Orders */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="250">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ShieldCheck className="text-danger me-2" />
                <h6 className="mb-0">Orders & Payments</h6>
              </div>
              <p className="text-muted small mb-2">Track & verify orders.</p>
              <button className="btn btn-sm btn-link px-0 text-danger" onClick={() => (window.location.href = "/admin/orders")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Queries */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="250">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ShieldCheck className="text-danger me-2" />
                <h6 className="mb-0">Queries</h6>
              </div>
              <p className="text-muted small mb-2">User support messages.</p>
              <button className="btn btn-sm btn-link px-0 text-danger" onClick={() => (window.location.href = "/admin/contacts")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Blogs */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="250">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ListTree className="text-danger me-2" />
                <h6 className="mb-0">Blogs</h6>
              </div>
              <p className="text-muted small mb-2">Create & edit posts.</p>
              <button className="btn btn-sm btn-link px-0 text-danger" onClick={() => (window.location.href = "/admin/blogs")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Price Slabs */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="260">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <ListTree className="text-info me-2" />
                <h6 className="mb-0">Price Slabs</h6>
              </div>
              <p className="text-muted small mb-2">Manage product bulk pricing slabs.</p>
              <button className="btn btn-sm btn-link px-0" onClick={() => (window.location.href = "/admin/price-slabs")}>
                Manage →
              </button>
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="col-6 col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay="300">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <BarChart2 className="text-warning me-2" />
                <h6 className="mb-0">Analytics</h6>
              </div>
              <p className="text-muted small mb-2">Insights & reports.</p>
              <button className="btn btn-sm btn-link px-0 text-warning" onClick={() => (window.location.href = "/admin/insights")}>
                View →
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
